import React from 'react';
import { createPortal } from 'react-dom';
import useMediaQuery from '../hooks/useMediaQuery';
import { useVisualViewportBox } from '../hooks/useVisualViewportBox';
import { getFloatingMenuStyle, getFloatingMenuZIndex } from '../lib/floatingMenu';
import './SearchableMultiSelect.css';

export type MultiSelectOption = {
  id: string;
  label: string;
  helper?: string;
  disabled?: boolean;
  /** Consecutive options with the same section are listed under its heading, with a line above it. */
  section?: string;
};

type Props = {
  options: MultiSelectOption[];
  selected: string[];
  onChange: (ids: string[]) => void;
  placeholder?: string;
  ariaLabel?: string;
  className?: string;
  selectionSummary?: string;
  showPills?: boolean;
  selectionMode?: 'toggle' | 'action';
  actionLabel?: string;
  bulkActions?: boolean;
  onQueryChange?: (query: string) => void;
  /** A row under the options, such as "2 closed sites match · Show". */
  menuFooter?: React.ReactNode;
  /** Remote lists can distinguish loading, failure and an empty search. */
  emptyMessage?: string;
  clearable?: boolean;
  /**
   * On phones (640px and narrower) the list opens as a bottom sheet with this title and its own
   * search, kept inside the visible area so it sits above the on-screen keyboard. Without it the
   * list is the floating menu at every width.
   */
  phoneSheetTitle?: string;
};

/**
 * Searchable multi-select combobox.
 *
 * - Type to filter the option list
 * - Click an option to toggle it on/off
 * - Selected items render as small pills below the input
 * - Click the X on a pill to deselect
 */
export default function SearchableMultiSelect({
  options,
  selected,
  onChange,
  placeholder = 'Search...',
  ariaLabel,
  className,
  selectionSummary,
  showPills = true,
  selectionMode = 'toggle',
  actionLabel = 'Add',
  bulkActions = false,
  onQueryChange,
  menuFooter,
  emptyMessage = 'No matches',
  clearable = false,
  phoneSheetTitle,
}: Props) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [, updateMenuPosition] = React.useReducer((version: number) => version + 1, 0);
  const wrapRef = React.useRef<HTMLDivElement | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const menuRef = React.useRef<HTMLDivElement | null>(null);
  const sheetInputRef = React.useRef<HTMLInputElement | null>(null);
  const menuId = React.useId();
  const sheetTitleId = React.useId();
  const isActionMode = selectionMode === 'action';
  const phoneWidth = useMediaQuery('(max-width: 640px)');
  const asSheet = Boolean(phoneSheetTitle) && phoneWidth;
  const sheetOpen = asSheet && open;
  const visibleArea = useVisualViewportBox(sheetOpen);

  // Typing happens in the sheet's own search, next to its list.
  React.useEffect(() => {
    if (sheetOpen) sheetInputRef.current?.focus();
  }, [sheetOpen]);

  function close() {
    setOpen(false);
    setQuery('');
    onQueryChange?.('');
  }

  const selectedSet = React.useMemo(() => new Set(selected), [selected]);

  const filtered = React.useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return options;
    return options.filter((o) => (
      o.label.toLowerCase().includes(term)
      || (o.helper ?? '').toLowerCase().includes(term)
    ));
  }, [query, options]);

  // Close on outside click
  React.useEffect(() => {
    function handler(e: MouseEvent) {
      const target = e.target as Node;
      if (wrapRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
      setQuery('');
      onQueryChange?.('');
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onQueryChange]);

  // The listbox is portalled out of toolbar scroll/clipping containers. Keep
  // its fixed position anchored to the input while the page or viewport moves.
  React.useEffect(() => {
    if (!open) return undefined;
    const reposition = () => updateMenuPosition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, { capture: true, passive: true });
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open]);

  function toggle(id: string) {
    const option = options.find((o) => o.id === id);
    if (option?.disabled && !selectedSet.has(id)) {
      inputRef.current?.focus();
      return;
    }
    const nextSelected = selectedSet.has(id)
      ? selected.filter((s) => s !== id)
      : [...selected, id];
    onChange(nextSelected);
    if (isActionMode) {
      // The sheet stays open for the next pick; Done closes it.
      if (asSheet) {
        sheetInputRef.current?.focus();
        return;
      }
      close();
      return;
    }
    (asSheet ? sheetInputRef : inputRef).current?.focus();
  }

  function remove(id: string) {
    onChange(selected.filter((s) => s !== id));
  }

  const selectedLabels = React.useMemo(() => {
    const map = new Map(options.map((o) => [o.id, o.label]));
    return selected.map((id) => ({ id, label: map.get(id) ?? id }));
  }, [selected, options]);

  const selectedSummaryText = selectionSummary ?? `${selected.length} selected`;
  const hasSelectionSummary = selected.length > 0 && query.length === 0 && !open;
  const placeholderText = hasSelectionSummary ? '' : placeholder;
  const wrapClassName = [
    'sms-wrap',
    className,
    hasSelectionSummary ? 'sms-wrap--summary' : '',
  ].filter(Boolean).join(' ');
  const menuWidth = typeof window === 'undefined'
    ? 260
    : Math.min(
      Math.max(inputRef.current?.getBoundingClientRect().width ?? 0, 260),
      Math.max(1, window.innerWidth - 16),
    );
  const floatingMenuStyle = getFloatingMenuStyle(inputRef.current, menuWidth);
  const menuStyle: React.CSSProperties = {
    ...floatingMenuStyle,
    top: floatingMenuStyle.top ?? 'auto',
    right: 'auto',
    bottom: floatingMenuStyle.bottom ?? 'auto',
    width: menuWidth,
    minWidth: menuWidth,
    maxWidth: 'calc(100vw - 16px)',
    maxHeight: 'min(220px, calc(100vh - 16px))',
  };

  const renderOption = (opt: MultiSelectOption) => {
    const isSelected = selectedSet.has(opt.id);
    const isDisabled = Boolean(opt.disabled && !isSelected);
    const optionClassName = [
      'sms-option',
      isSelected ? 'sms-option--active' : '',
      isDisabled ? 'sms-option--disabled' : '',
    ].filter(Boolean).join(' ');
    const optionContent = (
      <span className="sms-option__content">
        <span className="sms-option__label">{opt.label}</span>
        {opt.helper ? <small>{opt.helper}</small> : null}
      </span>
    );
    if (isActionMode) {
      return (
        <button
          key={opt.id}
          type="button"
          role="option"
          aria-selected={isSelected}
          aria-disabled={isDisabled}
          disabled={isDisabled}
          className={optionClassName}
          onClick={() => toggle(opt.id)}
        >
          {optionContent}
          <span className="sms-option__action">{actionLabel}</span>
        </button>
      );
    }
    return (
      <label
        key={opt.id}
        role="option"
        aria-selected={isSelected}
        aria-disabled={isDisabled}
        className={optionClassName}
      >
        <input
          type="checkbox"
          className="sms-checkbox"
          checked={isSelected}
          disabled={isDisabled}
          onChange={() => toggle(opt.id)}
        />
        {optionContent}
      </label>
    );
  };

  // Options that carry a section are grouped under its heading, in the order given.
  const sections = filtered.some((opt) => opt.section)
    ? filtered.reduce<Array<{ title: string; options: MultiSelectOption[] }>>((groups, opt) => {
      const title = opt.section ?? '';
      const last = groups[groups.length - 1];
      if (last && last.title === title) last.options.push(opt);
      else groups.push({ title, options: [opt] });
      return groups;
    }, [])
    : null;

  const listbox = (
    <div id={menuId} role="listbox" aria-label={ariaLabel} aria-multiselectable="true">
      {filtered.length === 0 ? (
        <div className="sms-empty">{emptyMessage}</div>
      ) : sections ? (
        sections.map((group, index) => (
          <div key={`${index}:${group.title}`} role="group" aria-labelledby={group.title ? `${menuId}-section-${index}` : undefined} className="sms-section">
            {group.title ? <div className="sms-section__title" id={`${menuId}-section-${index}`}>{group.title}</div> : null}
            {group.options.map(renderOption)}
          </div>
        ))
      ) : (
        filtered.map(renderOption)
      )}
    </div>
  );

  return (
    <div ref={wrapRef} className={wrapClassName}>
      {/* The summary centres on the input alone, not on the input plus the pills below it. */}
      <span className="sms-field">
        <input
          ref={inputRef}
          className="control-sm sms-input"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          aria-controls={open ? menuId : undefined}
          aria-label={ariaLabel}
          placeholder={placeholderText}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            onQueryChange?.(e.target.value);
            setOpen(true);
          }}
          onClick={() => setOpen(true)}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              // An open list owns Escape, so the dialog or page around it does not close too.
              if (open) {
                e.preventDefault();
                e.stopPropagation();
              }
              setOpen(false);
              setQuery('');
              onQueryChange?.('');
              inputRef.current?.blur();
            }
          }}
        />
        {hasSelectionSummary && (
          <span
            className="sms-selection-value"
            aria-hidden="true"
          >
            {selectedSummaryText}
          </span>
        )}
      </span>
      {clearable && query && !sheetOpen ? <button type="button" className="sms-clear-query"
        aria-label={`Clear ${ariaLabel || 'search'}`} onClick={() => {
          setQuery(''); onQueryChange?.(''); (asSheet ? sheetInputRef : inputRef).current?.focus();
        }}>&times;</button> : null}
      {/* Inside a native modal <dialog> (EditorDialog) the menu must join the dialog in the top
          layer; portalled to <body> it would sit behind the dialog and take no clicks. */}
      {open && !asSheet && typeof document !== 'undefined' ? createPortal(
        <div
          ref={menuRef}
          className="sms-menu"
          style={menuStyle}
        >
          {bulkActions && !isActionMode ? (
            <div className="sms-menu__bulk" role="group" aria-label={`${ariaLabel || 'Filter'} selection actions`}>
              <button type="button" disabled={!filtered.some(option => !option.disabled)} onClick={() => {
                onChange(Array.from(new Set([...selected, ...filtered.filter(option => !option.disabled).map(option => option.id)])));
                inputRef.current?.focus();
              }}>{query.trim() ? 'Select matches' : 'Select all'}</button>
              <button type="button" disabled={!selected.length} onClick={() => { onChange([]); setQuery(''); inputRef.current?.focus(); }}>Clear all</button>
            </div>
          ) : null}
          {listbox}
          {menuFooter}
        </div>,
        wrapRef.current?.closest('dialog[open]') ?? document.body,
      ) : null}
      {/* On a phone the list is a bottom sheet over the visible area, so the on-screen keyboard never
          covers it or its search; the same outside-tap rule closes it. */}
      {sheetOpen && typeof document !== 'undefined' ? createPortal(
        <div
          className="topbar-mobile-account-portal sms-sheet"
          style={{
            zIndex: getFloatingMenuZIndex(inputRef.current),
            ...(visibleArea ? { top: visibleArea.top, height: visibleArea.height, bottom: 'auto' } : null),
          }}
        >
          <div className="topbar-mobile-account-overlay sms-sheet__overlay" aria-hidden="true" />
          <div
            ref={menuRef}
            className="topbar-mobile-account-panel sms-sheet__panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby={sheetTitleId}
          >
            <div className="topbar-mobile-account-panel__handle" aria-hidden="true" />
            <div className="topbar-mobile-account-panel__header">
              <h2 id={sheetTitleId} className="topbar-mobile-account-panel__title">{phoneSheetTitle}</h2>
              <button type="button" className="btn sm" onClick={close}>Done</button>
            </div>
            <div className="sms-sheet__search">
              {clearable && query ? <button type="button" className="btn subtle sm"
                aria-label={`Clear ${ariaLabel || 'search'}`} onClick={() => {
                  setQuery(''); onQueryChange?.(''); sheetInputRef.current?.focus();
                }}>Clear search</button> : null}
              <input
                ref={sheetInputRef}
                className="control sms-sheet__input"
                type="search"
                aria-label={`Search: ${ariaLabel || phoneSheetTitle}`}
                aria-controls={menuId}
                placeholder={placeholder}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  onQueryChange?.(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    e.stopPropagation();
                    close();
                  }
                }}
              />
            </div>
            <div className="sms-sheet__list">
              {listbox}
              {menuFooter}
            </div>
          </div>
        </div>,
        wrapRef.current?.closest('dialog[open]') ?? document.body,
      ) : null}
      {showPills && selectedLabels.length > 0 && (
        <div className="sms-pills">
          {selectedLabels.map((s) => (
            <span key={s.id} className="sms-pill">
              {s.label}
              <button
                type="button"
                className="sms-pill__remove"
                onClick={() => remove(s.id)}
                aria-label={`Remove ${s.label}`}
              >
                &times;
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
